/**
 * Simplified assert + route helpers.
 *
 * Focus:
 * - Core throwing assertions (`assert*`)
 * - `test()` → turn throwing assertion(s) into boolean
 * - `expect()` → run assertions over a value and return the value
 * - `fork()` / `forkAsync()` → attach pass/fail handlers
 * - `fn()` / `fnAsync()` → wrap a function so
 *   AssertError in the callback yields a default value.
 */

// =====================
// Error type
// =====================

export class AssertError extends Error {
  readonly code = 'ASSERT_FAILED' as const;
  readonly info?: Record<string, unknown>;
  constructor(message = 'Assertion failed', info?: Record<string, unknown>) {
    super(message);
    this.name = 'AssertError';
    this.info = info;
  }
}

export type AssertErrorListener = (error: AssertError) => void;

const assertErrorListeners = new Set<AssertErrorListener>();

export const assertErrorEmitter = {
  on(listener: AssertErrorListener) {
    assertErrorListeners.add(listener);
    return () => {
      assertErrorListeners.delete(listener);
    };
  },
  off(listener: AssertErrorListener) {
    assertErrorListeners.delete(listener);
  },
  once(listener: AssertErrorListener) {
    const wrapped: AssertErrorListener = (error) => {
      assertErrorListeners.delete(wrapped);
      listener(error);
    };
    assertErrorListeners.add(wrapped);
    return () => {
      assertErrorListeners.delete(wrapped);
    };
  },
  emit(error: AssertError) {
    for (const listener of Array.from(assertErrorListeners)) {
      try {
        listener(error);
      } catch {
        // ignore listener errors to keep assertion semantics stable
      }
    }
    return error;
  },
} as const;

function emitAssertError(error: AssertError): AssertError {
  return assertErrorEmitter.emit(error);
}

// =====================
// Core assert
// =====================

export function assert(condition: unknown, message = 'Assertion failed', info?: Record<string, unknown>): asserts condition {
  if (!condition) throw emitAssertError(new AssertError(message, info));
}

// =====================
// Assertion helpers (type guards that throw)
// =====================
export function isFunction(x: unknown): x is (...args: unknown[]) => unknown {
  return typeof x === 'function';
}

/** Returns true if x is Promise-like (has a then method). */
export function isPromiseLike<T = unknown>(x: unknown): x is PromiseLike<T> {
  return x != null && typeof (x as Promise<unknown>).then === 'function';
}

export function assertString(x: unknown, message = 'Expected string'): asserts x is string {
  assert(typeof x === 'string', message);
}

export function assertNumber(x: unknown, message = 'Expected finite number'): asserts x is number {
  assert(typeof x === 'number' && Number.isFinite(x), message);
}

export function assertBoolean(x: unknown, message = 'Expected boolean'): asserts x is boolean {
  assert(typeof x === 'boolean', message);
}

export function assertArray<T = unknown>(x: unknown, message = 'Expected array'): asserts x is T[] {
  assert(Array.isArray(x), message);
}

export function assertObject(x: unknown, message = 'Expected object'): asserts x is Record<string, unknown> {
  // Accept arrays as objects; only exclude null.
  assert(typeof x === 'object' && x !== null, message);
}

/** Asserts that x is a plain (non-array) object. */
export function assertPlainObject(x: unknown, message = 'Expected plain object'): asserts x is Record<string, unknown> {
  assert(typeof x === 'object' && x !== null && !Array.isArray(x), message);
}

/** Ensure a plain (non-array) object and return it typed. */
export function expectPlainObject(x: unknown, message?: string): Record<string, unknown> {
  assertPlainObject(x, message ?? 'Expected plain object');
  return x as Record<string, unknown>;
}

export function assertDate(x: unknown, message = 'Expected valid Date'): asserts x is Date {
  assert(x instanceof Date && !Number.isNaN(x.getTime?.()), message);
}

export function assertNonNull<T>(x: T | null, message = 'Expected non-null'): asserts x is T {
  assert(x !== null, message);
}

export function assertExists<T>(x: T | null | undefined, message = 'Expected value (not null/undefined)'): asserts x is T {
  assert(x !== null && x !== undefined, message);
}
/**
 * Asserts that x is a function.
 *
 * Narrowing:
 * - On success, narrows x to (...args:any[]) => unknown.
 */
export function assertFunction(x: unknown, message = 'Expected function', info?: Record<string, unknown>): asserts x is (...args: unknown[]) => unknown {
  assert(isFunction(x), message, { ...info, got: typeof x });
}
export function isDefined<T>(x: T | undefined): x is T {
  return x !== undefined;
}

export function isNonNull<T>(x: T | null): x is T {
  return x !== null;
}
export function isPresent<T>(x: T | null | undefined): x is T {
  return x !== null && x !== undefined;
}

export function isNotNil<T>(x: T | null | undefined): x is T {
  return x !== null && x !== undefined;
}

/**
 * Asserts that x is Promise-like (has a then method).
 *
 * Narrowing:
 * - On success, narrows x to PromiseLike<unknown>.
 */
export function assertPromiseLike<T = unknown>(x: unknown, message = 'Expected Promise-like', info?: Record<string, unknown>): asserts x is PromiseLike<T> {
  assert(isPromiseLike<T>(x), message, { ...info, got: typeof x });
}

export function assertDefined<T>(x: T | undefined, message = 'Expected defined', info?: Record<string, unknown>): asserts x is T {
  assert(isDefined(x), message, info);
}

export function assertPresent<T>(x: T | null | undefined, message = 'Expected value present', info?: Record<string, unknown>): asserts x is T {
  assert(isPresent(x), message, info);
}

/** Assert that a value is not null or undefined (aka not nil). */
export function assertNotNil<T>(x: T | null | undefined, message = 'Expected value not nil'): asserts x is T {
  assert(isNotNil(x), message);
}

// =====================
// expect() and convenience expect* helpers
// =====================

/**
 * Expect overloads:
 * - Factory: given an assertion function, return an `expectX`-style function that
 *   runs the assertion and returns the narrowed value.
 * - Value mode: run one or more assertion functions against a value and return the value.
 */
export function expect<T, A extends unknown[]>(assertFn: (x: unknown, ...args: A) => asserts x is T): (x: unknown, ...args: A) => T;
export function expect<V>(value: V, ...assertions: Array<(v: V) => void>): V;
export function expect(...args: unknown[]): unknown {
  const first = args[0] as unknown;
  if (typeof first === 'function') {
    const assertFn = first as (x: unknown, ...rest: unknown[]) => void;
    return (x: unknown, ...rest: unknown[]) => {
      assertFn(x, ...rest);
      return x as unknown;
    };
  }
  const value = first as unknown;
  const assertions = args.slice(1) as Array<(v: unknown) => void>;
  for (const a of assertions) a(value);
  return value;
}

export function expectString(x: unknown, message?: string): string {
  assertString(x, message);
  return x as string;
}
export function expectNumber(x: unknown, message?: string): number {
  assertNumber(x, message);
  return x as number;
}
export function expectBoolean(x: unknown, message?: string): boolean {
  assertBoolean(x, message);
  return x as boolean;
}
export function expectArray<T = unknown>(x: unknown, message?: string): T[] {
  assertArray<T>(x, message);
  return x as T[];
}
export function expectArrayNotEmpty<T = unknown>(x: unknown, message?: string): T[] {
  assertArrayNotEmpty<T>(x, message);
  return x as T[];
}
export function expectObject(x: unknown, message?: string): Record<string, unknown> {
  assertObject(x, message);
  return x as Record<string, unknown>;
}
export function expectDate(x: unknown, message?: string): Date {
  assertDate(x, message);
  return x as Date;
}
export function expectNonNull<T>(x: T | null, message?: string): T {
  assertNonNull(x, message);
  return x as T;
}
export function expectExists<T>(x: T | null | undefined, message?: string): T {
  assertExists(x, message);
  return x as T;
}

// ---- Additional expect helpers ----

export function expectFunction<F extends (...args: unknown[]) => unknown>(x: unknown, message?: string): F {
  assertFunction(x, message);
  return x as F;
}

export function expectPromiseLike<T = unknown>(x: unknown, message?: string): PromiseLike<T> {
  assertPromiseLike<T>(x, message);
  return x as PromiseLike<T>;
}

export function expectDefined<T>(x: T | undefined, message?: string): T {
  assertDefined(x, message);
  return x as T;
}

export function expectPresent<T>(x: T | null | undefined, message?: string): T {
  assertPresent(x, message);
  return x as T;
}

/** Ensure value is not null or undefined and return it. */
export function expectNotNil<T>(x: T | null | undefined, message?: string): T {
  assertNotNil(x, message);
  return x as T;
}

export function expectTruthy<T>(x: T, message?: string): Exclude<T, null | undefined | false | 0 | ''> {
  assertTruthy(x, message);
  return x as Exclude<T, null | undefined | false | 0 | ''>;
}

export function expectFalsey(x: unknown, message?: string): null | undefined | false | 0 | '' {
  assertFalsey(x, message);
  return x as null | undefined | false | 0 | '';
}

// String expects
export function expectStringLength(x: unknown, len: number, message?: string): string {
  assertStringLength(x, len, message);
  return x as string;
}
export function expectStringLengthAtLeast(x: unknown, n: number, message?: string): string {
  assertStringLengthAtLeast(x, n, message);
  return x as string;
}
export function expectStringLengthAtMost(x: unknown, n: number, message?: string): string {
  assertStringLengthAtMost(x, n, message);
  return x as string;
}
export function expectStringLengthBetween(x: unknown, min: number, max: number, message?: string): string {
  assertStringLengthBetween(x, min, max, message);
  return x as string;
}
export function expectStringContains(x: unknown, needle: string | RegExp, message?: string): string {
  assertStringContains(x, needle, message);
  return x as string;
}
export function expectStringStartsWith(x: unknown, prefix: string, message?: string): string {
  assertStringStartsWith(x, prefix, message);
  return x as string;
}
export function expectStringEndsWith(x: unknown, suffix: string, message?: string): string {
  assertStringEndsWith(x, suffix, message);
  return x as string;
}
export function expectStringMatches(x: unknown, re: RegExp, message?: string): string {
  assertStringMatches(x, re, message);
  return x as string;
}
export function expectStringEqualsIgnoreCase(x: unknown, expected: string, message?: string): string {
  assertStringEqualsIgnoreCase(x, expected, message);
  return x as string;
}
export function expectStringIncludesAny(x: unknown, ...needles: string[]): string {
  assertStringIncludesAny(x, ...needles);
  return x as string;
}
export function expectStringIncludesAll(x: unknown, ...needles: string[]): string {
  assertStringIncludesAll(x, ...needles);
  return x as string;
}
export function expectStringIsJSON(x: unknown, message?: string): string {
  assertStringIsJSON(x, message);
  return x as string;
}
export function expectStringTrimmedNotEmpty(x: unknown, message?: string): string {
  assertStringTrimmedNotEmpty(x, message);
  return x as string;
}

// Non-empty expects
export function expectNonEmptyString(x: unknown, message?: string): string {
  assertString(x, message);
  assert((x as string).length > 0, message ?? 'Expected non-empty string');
  return x as string;
}
export function expectNonEmptyArray<T = unknown>(x: unknown, message?: string): T[] {
  assertArrayNotEmpty<T>(x, message);
  return x as T[];
}

// Number expects
export function expectNumberGreaterThan(x: unknown, n: number, message?: string): number {
  assertNumberGreaterThan(x, n, message);
  return x as number;
}
export function expectNumberGreaterOrEqual(x: unknown, n: number, message?: string): number {
  assertNumberGreaterOrEqual(x, n, message);
  return x as number;
}
export function expectNumberLessThan(x: unknown, n: number, message?: string): number {
  assertNumberLessThan(x, n, message);
  return x as number;
}
export function expectNumberLessOrEqual(x: unknown, n: number, message?: string): number {
  assertNumberLessOrEqual(x, n, message);
  return x as number;
}
export function expectNumberBetween(x: unknown, min: number, max: number, message?: string): number {
  assertNumberBetween(x, min, max, message);
  return x as number;
}
export function expectNumberNotZero(x: unknown, message?: string): number {
  assertNumberNotZero(x, message);
  return x as number;
}
export function expectNumberPositive(x: unknown, message?: string): number {
  assertNumberPositive(x, message);
  return x as number;
}
export function expectNumberNonNegative(x: unknown, message?: string): number {
  assertNumberNonNegative(x, message);
  return x as number;
}
export function expectNumberNegative(x: unknown, message?: string): number {
  assertNumberNegative(x, message);
  return x as number;
}
export function expectNumberNonPositive(x: unknown, message?: string): number {
  assertNumberNonPositive(x, message);
  return x as number;
}
export function expectNumberInteger(x: unknown, message?: string): number {
  assertNumberInteger(x, message);
  return x as number;
}
export function expectNumberSafeInteger(x: unknown, message?: string): number {
  assertNumberSafeInteger(x, message);
  return x as number;
}
export function expectNumberApproxEquals(x: unknown, expected: number, epsilon?: number, message?: string): number {
  assertNumberApproxEquals(x, expected, epsilon, message);
  return x as number;
}

// Array expects
export function expectArrayLength<T = unknown>(x: unknown, len: number, message?: string): T[] {
  assertArrayLength<T>(x, len, message);
  return x as T[];
}
export function expectArrayHasAnyOf<T = unknown>(x: unknown, items: string[], message?: string): T[] {
  assertArrayHasAnyOf<T>(x, items, message);
  return x as T[];
}
export function expectArrayHasEveryOf<T = unknown>(x: unknown, items: string[], message?: string): T[] {
  assertArrayHasEveryOf<T>(x, items, message);
  return x as T[];
}
export function expectArrayItemIsBoolean<T = unknown>(x: unknown, i: number, message?: string): T[] {
  assertArrayItemIsBoolean<T>(x, i, message);
  return x as T[];
}
export function expectArrayItemIsString<T = unknown>(x: unknown, i: number, message?: string): T[] {
  assertArrayItemIsString<T>(x, i, message);
  return x as T[];
}
export function expectArrayItemIsNumber<T = unknown>(x: unknown, i: number, message?: string): T[] {
  assertArrayItemIsNumber<T>(x, i, message);
  return x as T[];
}
export function expectArrayItemIsObject<T = unknown>(x: unknown, i: number, message?: string): T[] {
  assertArrayItemIsObject<T>(x, i, message);
  return x as T[];
}
export function expectArrayIncludesString<T = unknown>(x: unknown, needle: string, message?: string): T[] {
  assertArrayIncludesString<T>(x, needle, message);
  return x as T[];
}
export function expectArrayIncludesNumber<T = unknown>(x: unknown, needle: number, message?: string): T[] {
  assertArrayIncludesNumber<T>(x, needle, message);
  return x as T[];
}
export function expectArrayIncludesObject<T = unknown>(x: unknown, needle: Record<string, unknown>, message?: string): T[] {
  assertArrayIncludesObject<T>(x, needle, message);
  return x as T[];
}
export function expectArrayOnlyHasObjects(x: unknown, message?: string): Record<string, unknown>[] {
  assertArrayOnlyHasObjects(x, message);
  return x as Record<string, unknown>[];
}
export function expectArrayOnlyHasStrings(x: unknown, message?: string): string[] {
  assertArrayOnlyHasStrings(x, message);
  return x as string[];
}
export function expectArrayOnlyHasNumbers(x: unknown, message?: string): number[] {
  assertArrayOnlyHasNumbers(x, message);
  return x as number[];
}
export function expectArrayEveryIsFalsy<T = unknown>(x: unknown, message?: string): T[] {
  assertArrayEveryIsFalsy<T>(x, message);
  return x as T[];
}
export function expectArrayEveryIsTruthy<T = unknown>(x: unknown, message?: string): T[] {
  assertArrayEveryIsTruthy<T>(x, message);
  return x as T[];
}
export function expectArrayUnique<T = unknown>(x: unknown, message?: string): T[] {
  assertArrayUnique<T>(x, message);
  return x as T[];
}
export function expectArrayIncludesCondition<T = unknown>(x: unknown, predicate: (item: unknown) => boolean, message?: string): T[] {
  assertArrayIncludesCondition<T>(x, predicate, message);
  return x as T[];
}

// Object expects
export function expectHasKey<O extends Record<string, unknown>, K extends string>(obj: unknown, key: K, message?: string): O & Record<K, unknown> {
  assertHasKey<O, K>(obj, key, message);
  return obj as O & Record<K, unknown>;
}
export function expectHasKeys<O extends Record<string, unknown>, const K extends readonly string[]>(obj: unknown, ...keys: K): O & { [P in K[number]]: unknown } {
  assertHasKeys<O, K>(obj, ...keys);
  return obj as O & { [P in K[number]]: unknown };
}
export function expectKeyEquals<O extends Record<string, unknown>, K extends keyof O, E>(obj: unknown, key: K, expected: E, message?: string): O & Record<K, E> {
  assertKeyEquals<O, K, E>(obj, key, expected, message);
  return obj as O & Record<K, E>;
}
export function expectSameKeys(obj: unknown, expected: Record<string, unknown>, message?: string): Record<string, unknown> {
  assertSameKeys(obj, expected, message);
  return obj as Record<string, unknown>;
}
export function expectAllKeysFalsy(obj: unknown, message?: string): Record<string, unknown> {
  assertAllKeysFalsy(obj, message);
  return obj as Record<string, unknown>;
}
export function expectAllKeysSet(obj: unknown, message?: string): Record<string, unknown> {
  assertAllKeysSet(obj, message);
  return obj as Record<string, unknown>;
}
export function expectAnyKeyNull(obj: unknown, message?: string): Record<string, unknown> {
  assertAnyKeyNull(obj, message);
  return obj as Record<string, unknown>;
}

// Element expects
export function expectElement(x: unknown, message?: string): Element {
  assertElement(x, message);
  return x as Element;
}
export function expectHTMLElement(x: unknown, message?: string): HTMLElement {
  assertHTMLElement(x, message);
  return x as HTMLElement;
}

export function expectElementTags<K extends keyof HTMLElementTagNameMap>(x: unknown, tags: readonly K[], message?: string): HTMLElementTagNameMap[K] {
  const el = expectElement(x, message);
  const t = el.tagName.toLowerCase() as K;

  if (!tags.includes(t)) {
    throw new Error(message ?? `Expected one of ${tags.join(', ')}, got <${t}>`);
  }
  return el as HTMLElementTagNameMap[K];
}

export const expectDiv = (x: unknown, m?: string) => expectElementTags(x, ['div'], m);

export const expectSpan = (x: unknown, m?: string) => expectElementTags(x, ['span'], m);

export const expectP = (x: unknown, m?: string) => expectElementTags(x, ['p'], m);

export const expectUl = (x: unknown, m?: string) => expectElementTags(x, ['ul'], m);

export const expectOl = (x: unknown, m?: string) => expectElementTags(x, ['ol'], m);

export const expectLi = (x: unknown, m?: string) => expectElementTags(x, ['li'], m);

export const expectForm = (x: unknown, m?: string) => expectElementTags(x, ['form'], m);

export const expectInput = (x: unknown, m?: string) => expectElementTags(x, ['input'], m);

export const expectTextarea = (x: unknown, m?: string) => expectElementTags(x, ['textarea'], m);

export const expectButton = (x: unknown, m?: string) => expectElementTags(x, ['button'], m);

export const expectSelect = (x: unknown, m?: string) => expectElementTags(x, ['select'], m);

export const expectOption = (x: unknown, m?: string) => expectElementTags(x, ['option'], m);

export const expectLabel = (x: unknown, m?: string) => expectElementTags(x, ['label'], m);

export const expectImg = (x: unknown, m?: string) => expectElementTags(x, ['img'], m);

export const expectVideo = (x: unknown, m?: string) => expectElementTags(x, ['video'], m);

export const expectAudio = (x: unknown, m?: string) => expectElementTags(x, ['audio'], m);

export const expectCanvas = (x: unknown, m?: string) => expectElementTags(x, ['canvas'], m);

export const expectTable = (x: unknown, m?: string) => expectElementTags(x, ['table'], m);

export const expectTHead = (x: unknown, m?: string) => expectElementTags(x, ['thead'], m);

export const expectTBody = (x: unknown, m?: string) => expectElementTags(x, ['tbody'], m);

export const expectTFoot = (x: unknown, m?: string) => expectElementTags(x, ['tfoot'], m);

export const expectTr = (x: unknown, m?: string) => expectElementTags(x, ['tr'], m);

export const expectTh = (x: unknown, m?: string) => expectElementTags(x, ['th'], m);

export const expectTd = (x: unknown, m?: string) => expectElementTags(x, ['td'], m);

export function expectSvgElement(x: unknown, message?: string): SVGElement {
  if (!(x instanceof SVGElement)) {
    throw new Error(message ?? 'Expected SVGElement');
  }
  return x;
}

export function expectSvgTag<K extends keyof SVGElementTagNameMap>(x: unknown, tag: K, message?: string): SVGElementTagNameMap[K] {
  const el = expectSvgElement(x, message);
  const t = el.tagName.toLowerCase() as K;

  if (t !== tag) {
    throw new Error(message ?? `Expected <${tag}>, got <${t}>`);
  }
  return el as SVGElementTagNameMap[K];
}
export const expectSvg = (x: unknown, m?: string) => expectSvgTag(x, 'svg', m);

export const expectSvgPath = (x: unknown, m?: string) => expectSvgTag(x, 'path', m);

export const expectSvgGroup = (x: unknown, m?: string) => expectSvgTag(x, 'g', m);

export const expectSvgCircle = (x: unknown, m?: string) => expectSvgTag(x, 'circle', m);

export const expectSvgRect = (x: unknown, m?: string) => expectSvgTag(x, 'rect', m);

export const expectSvgLine = (x: unknown, m?: string) => expectSvgTag(x, 'line', m);

export function expectElementHasChildren(x: unknown, message?: string): Element {
  assertElementHasChildren(x, message);
  return x as Element;
}
export function expectElementHasChild(x: unknown, message?: string): Element {
  assertElementHasChild(x, message);
  return x as Element;
}
export function expectElementHasChildMatching(x: unknown, selector: string, message?: string): Element {
  assertElementHasChildMatching(x, selector, message);
  return x as Element;
}
export function expectElementHasDescendant(x: unknown, selector: string, message?: string): Element {
  assertElementHasDescendant(x, selector, message);
  return x as Element;
}
export function expectElementHasAttribute(x: unknown, name: string, message?: string): Element {
  assertElementHasAttribute(x, name, message);
  return x as Element;
}
export function expectElementAttributeEquals(x: unknown, name: string, expected: string, message?: string): Element {
  assertElementAttributeEquals(x, name, expected, message);
  return x as Element;
}
export function expectElementHidden(x: unknown, message?: string): Element {
  assertElementHidden(x, message);
  return x as Element;
}
export function expectElementVisible(x: unknown, message?: string): Element {
  assertElementVisible(x, message);
  return x as Element;
}

// Date expects
export function expectDateEarlier(x: unknown, than: Date, message?: string): Date {
  assertDateEarlier(x, than, message);
  return x as Date;
}
export function expectDateLater(x: unknown, than: Date, message?: string): Date {
  assertDateLater(x, than, message);
  return x as Date;
}
export function expectDateBetween(x: unknown, min: Date, max: Date, message?: string): Date {
  assertDateBetween(x, min, max, message);
  return x as Date;
}
export function expectDateYear(x: unknown, year: number, message?: string): Date {
  assertDateYear(x, year, message);
  return x as Date;
}
export function expectDateBefore(x: unknown, than: Date, message?: string): Date {
  assertDateBefore(x, than, message);
  return x as Date;
}
export function expectDateAfter(x: unknown, than: Date, message?: string): Date {
  assertDateAfter(x, than, message);
  return x as Date;
}
export function expectDateOnOrBefore(x: unknown, than: Date, message?: string): Date {
  assertDateOnOrBefore(x, than, message);
  return x as Date;
}
export function expectDateOnOrAfter(x: unknown, than: Date, message?: string): Date {
  assertDateOnOrAfter(x, than, message);
  return x as Date;
}
export function expectDateBetweenInclusive(x: unknown, min: Date, max: Date, message?: string): Date {
  assertDateBetweenInclusive(x, min, max, message);
  return x as Date;
}
export function expectDateBetweenExclusive(x: unknown, min: Date, max: Date, message?: string): Date {
  assertDateBetweenExclusive(x, min, max, message);
  return x as Date;
}
export function expectDateInPast(x: unknown, message?: string): Date {
  assertDateInPast(x, message);
  return x as Date;
}
export function expectDateInFuture(x: unknown, message?: string): Date {
  assertDateInFuture(x, message);
  return x as Date;
}
export function expectDateWithinPast(x: unknown, ms: number, message?: string): Date {
  assertDateWithinPast(x, ms, message);
  return x as Date;
}
export function expectDateWithinFuture(x: unknown, ms: number, message?: string): Date {
  assertDateWithinFuture(x, ms, message);
  return x as Date;
}
export function expectDateSameYear(x: unknown, other: Date, message?: string): Date {
  assertDateSameYear(x, other, message);
  return x as Date;
}
export function expectDateSameMonth(x: unknown, other: Date, message?: string): Date {
  assertDateSameMonth(x, other, message);
  return x as Date;
}
export function expectDateSameDay(x: unknown, other: Date, message?: string): Date {
  assertDateSameDay(x, other, message);
  return x as Date;
}

// =====================
// test() → turn throwing assertions into boolean
// =====================

/**
 * Execute an assertion function; return true if it succeeds, false if it throws AssertError.
 * Non-AssertError exceptions are rethrown (to avoid swallowing real errors).
 */
export function test(assertion: () => void): boolean {
  try {
    assertion();
    return true;
  } catch (e) {
    if (e instanceof AssertError) return false;
    throw e;
  }
}

// =====================
// fork: pass/fail handlers
// =====================

export function fork(assertion: () => void, onPass: (...args: unknown[]) => void, onFail: (error: AssertError, ...args: unknown[]) => void, ...args: unknown[]): void {
  try {
    assertion();
    onPass(...args);
  } catch (e) {
    if (e instanceof AssertError) {
      onFail(e, ...args);
      return;
    }
    throw e; // non-assert error → rethrow
  }
}

export async function forkAsync(assertion: () => Promise<void>, onPass: (...args: unknown[]) => Promise<void>, onFail: (error: AssertError, ...args: unknown[]) => Promise<void>, ...args: unknown[]): Promise<void> {
  try {
    await assertion();
    await onPass(...args);
  } catch (e) {
    if (e instanceof AssertError) {
      await onFail(e, ...args);
      return;
    }
    throw e;
  }
}

// =====================
// fn / fnAsync
// =====================

export type AssertFnOptions = {
  onError?: (err: AssertError) => void;
  catchNonAssertErrors?: boolean; // default false: rethrow non-AssertError
};

/**
 * Wrap a function; if an AssertError occurs inside the callback, return the provided default.
 * - `defaultValue` can be a value or a thunk receiving `(error, ...args)`
 * - Non-Assert errors are rethrown
 */
export function fn<R, A extends unknown[]>(defaultValue: R | ((error: AssertError, ...args: A) => R), callback: (...args: A) => R, options?: AssertFnOptions): (...args: A) => R {
  const { onError, catchNonAssertErrors = false } = options ?? {};
  return (...args: A) => {
    try {
      return callback(...args);
    } catch (e) {
      if (e instanceof AssertError) {
        emitAssertError(e);
        onError?.(e);
        return typeof defaultValue === 'function' ? (defaultValue as (error: AssertError, ...args: A) => R)(e, ...args) : defaultValue;
      }
      if (catchNonAssertErrors) {
        const err = e instanceof Error ? e : new Error(String(e));
        const ae = new AssertError(err.message);
        emitAssertError(ae);
        onError?.(ae);
        return typeof defaultValue === 'function' ? (defaultValue as (error: AssertError, ...args: A) => R)(ae, ...args) : defaultValue;
      }
      throw e;
    }
  };
}

export function fnAsync<R, A extends unknown[]>(defaultValue: R | ((error: AssertError, ...args: A) => R | Promise<R>), callback: (...args: A) => Promise<R>, options?: AssertFnOptions): (...args: A) => Promise<R> {
  const { onError, catchNonAssertErrors = false } = options ?? {};
  return async (...args: A) => {
    try {
      return await callback(...args);
    } catch (e) {
      if (e instanceof AssertError) {
        emitAssertError(e);
        onError?.(e);
        return typeof defaultValue === 'function' ? await (defaultValue as (error: AssertError, ...args: A) => R | Promise<R>)(e, ...args) : (defaultValue as R);
      }
      if (catchNonAssertErrors) {
        const err = e instanceof Error ? e : new Error(String(e));
        const ae = new AssertError(err.message);
        emitAssertError(ae);
        onError?.(ae);
        const dv = typeof defaultValue === 'function' ? (defaultValue as (error: AssertError, ...args: A) => R | Promise<R>)(ae, ...args) : (defaultValue as R);
        return await Promise.resolve(dv);
      }
      throw e;
    }
  };
}

/**
 * Immediate exec, zero-arg: run `cb` now. On AssertError, return `fallback`.
 * Optionally catch non-Assert errors and observe via `onError`.
 */
export function fnRun<T>(fallback: T, cb: () => T, options?: AssertFnOptions): T {
  const { onError, catchNonAssertErrors = false } = options ?? {};
  try {
    return cb();
  } catch (e) {
    if (e instanceof AssertError) {
      emitAssertError(e);
      onError?.(e);
      return fallback;
    }
    if (catchNonAssertErrors) {
      const err = e instanceof Error ? e : new Error(String(e));
      const ae = new AssertError(err.message);
      emitAssertError(ae);
      onError?.(ae);
      return fallback;
    }
    throw e;
  }
}

/**
 * Immediate async exec, zero-arg: run `cb` now. On AssertError, resolve with `fallback`.
 * Optionally catch non-Assert errors and observe via `onError`.
 */
export async function fnRunAsync<T>(fallback: T, cb: () => Promise<T>, options?: AssertFnOptions): Promise<T> {
  const { onError, catchNonAssertErrors = false } = options ?? {};
  try {
    return await cb();
  } catch (e) {
    if (e instanceof AssertError) {
      emitAssertError(e);
      onError?.(e);
      return fallback;
    }
    if (catchNonAssertErrors) {
      const err = e instanceof Error ? e : new Error(String(e));
      const ae = new AssertError(err.message);
      emitAssertError(ae);
      onError?.(ae);
      return fallback;
    }
    throw e;
  }
}

// Prefer fn/fnAsync + fnRun/fnRunAsync for scoped fallback behavior.
export function assertStringLength(x: unknown, len: number, message?: string): asserts x is string {
  assertString(x, message ?? `Expected string length ${len}`);
  assert((x as string).length === len, message ?? `Expected string length ${len}`);
}

/** Asserts that x is a string with length >= `n`. */
export function assertStringLengthAtLeast(x: unknown, n: number, message?: string): asserts x is string {
  assertString(x, message ?? `Expected string length >= ${n}`);
  assert((x as string).length >= n, message ?? `Expected string length >= ${n}`);
}

/** Asserts that x is a string with length <= `n`. */
export function assertStringLengthAtMost(x: unknown, n: number, message?: string): asserts x is string {
  assertString(x, message ?? `Expected string length <= ${n}`);
  assert((x as string).length <= n, message ?? `Expected string length <= ${n}`);
}

/** Asserts that x is a string with min/max inclusive bounds. */
export function assertStringLengthBetween(x: unknown, min: number, max: number, message?: string): asserts x is string {
  assertString(x, message ?? `Expected string length between ${min} and ${max}`);
  const l = (x as string).length;
  assert(l >= min && l <= max, message ?? `Expected string length between ${min} and ${max}`);
}

/** Asserts that x is a string containing substring or matching regex. */
export function assertStringContains(x: unknown, needle: string | RegExp, message?: string): asserts x is string {
  assertString(x, message ?? `Expected string to contain ${String(needle)}`);
  const s = x as string;
  const ok = typeof needle === 'string' ? s.includes(needle) : needle.test(s);
  assert(ok, message ?? `Expected string to contain ${String(needle)}`);
}

/** Asserts that x is a string starting with the given prefix. */
export function assertStringStartsWith(x: unknown, prefix: string, message?: string): asserts x is string {
  assertString(x, message ?? `Expected string to start with "${prefix}"`);
  assert((x as string).startsWith(prefix), message ?? `Expected string to start with "${prefix}"`);
}

/** Asserts that x is a string ending with the given suffix. */
export function assertStringEndsWith(x: unknown, suffix: string, message?: string): asserts x is string {
  assertString(x, message ?? `Expected string to end with "${suffix}"`);
  assert((x as string).endsWith(suffix), message ?? `Expected string to end with "${suffix}"`);
}

/** Asserts that x is a string that matches the regex. */
export function assertStringMatches(x: unknown, re: RegExp, message?: string): asserts x is string {
  assertString(x, message ?? `Expected string to match ${re}`);
  assert(re.test(x as string), message ?? `Expected string to match ${re}`);
}

/** Asserts that x (string) equals `expected` ignoring case. */
export function assertStringEqualsIgnoreCase(x: unknown, expected: string, message?: string): asserts x is string {
  assertString(x, message ?? `Expected "${expected}" (case-insensitive)`);
  assert((x as string).toLowerCase() === expected.toLowerCase(), message ?? `Expected "${expected}" (case-insensitive)`);
}

/** Asserts that x is a string including any of the provided substrings. */
export function assertStringIncludesAny(x: unknown, ...needles: string[]): asserts x is string {
  assertString(x, `Expected string`);
  const s = x as string;
  assert(
    needles.some((n) => s.includes(n)),
    `Expected string to include any of [${needles.join(', ')}]`,
  );
}

/** Asserts that x is a string including all of the provided substrings. */
export function assertStringIncludesAll(x: unknown, ...needles: string[]): asserts x is string {
  assertString(x, `Expected string`);
  const s = x as string;
  assert(
    needles.every((n) => s.includes(n)),
    `Expected string to include all of [${needles.join(', ')}]`,
  );
}

/** Asserts that x is a string containing valid JSON. */
export function assertStringIsJSON(x: unknown, message = 'Expected valid JSON'): asserts x is string {
  assertString(x, message);
  try {
    JSON.parse(x as string);
  } catch {
    assert(false, message);
  }
}

/** Asserts that x is a string whose trimmed length > 0. */
export function assertStringTrimmedNotEmpty(x: unknown, message = 'Expected non-empty (trimmed)'): asserts x is string {
  assertString(x, message);
  assert((x as string).trim().length > 0, message);
}

// ---- Numbers ----

/** Asserts that x is a number strictly greater than n. */
export function assertNumberGreaterThan(x: unknown, n: number, message?: string): asserts x is number {
  assertNumber(x, message ?? `Expected > ${n}`);
  assert((x as number) > n, message ?? `Expected > ${n}`);
}

/** Asserts that x is a number >= n. */
export function assertNumberGreaterOrEqual(x: unknown, n: number, message?: string): asserts x is number {
  assertNumber(x, message ?? `Expected >= ${n}`);
  assert((x as number) >= n, message ?? `Expected >= ${n}`);
}

/** Asserts that x is a number strictly less than n. */
export function assertNumberLessThan(x: unknown, n: number, message?: string): asserts x is number {
  assertNumber(x, message ?? `Expected < ${n}`);
  assert((x as number) < n, message ?? `Expected < ${n}`);
}

/** Asserts that x is a number <= n. */
export function assertNumberLessOrEqual(x: unknown, n: number, message?: string): asserts x is number {
  assertNumber(x, message ?? `Expected <= ${n}`);
  assert((x as number) <= n, message ?? `Expected <= ${n}`);
}

/** Asserts that x is a number within [min, max]. */
export function assertNumberBetween(x: unknown, min: number, max: number, message?: string): asserts x is number {
  assertNumber(x, message ?? `Expected between ${min} and ${max}`);
  const v = x as number;
  assert(v >= min && v <= max, message ?? `Expected between ${min} and ${max}`);
}

// ---- Arrays ----

/** Asserts that x is an array with exact length `len`. */
export function assertArrayLength<T = unknown>(x: unknown, len: number, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array length ${len}`);
  assert((x as T[]).length === len, message ?? `Expected array length ${len}`);
}

/** Asserts that x is a non-empty array. */
export function assertArrayNotEmpty<T = unknown>(x: unknown, message = 'Expected non-empty array'): asserts x is T[] {
  assertArray<T>(x, 'Expected array');
  assert((x as T[]).length > 0, message);
}

/** Asserts that x is an array containing at least one of the provided items (by string form). */
export function assertArrayHasAnyOf<T = unknown>(x: unknown, items: string[], message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  const arr = x as unknown[];
  const set = new Set(items);
  const ok = arr.some((el) => set.has(String(el)));
  assert(ok, message ?? `Expected array to contain any of [${items.join(', ')}]`);
}

/** Asserts that x is an array containing all the provided items (by string form). */
export function assertArrayHasEveryOf<T = unknown>(x: unknown, items: string[], message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  const arr = x as unknown[];
  const set = new Set(arr.map((v) => (typeof v === 'string' ? v : String(v))));
  const missing = items.filter((k) => !set.has(k));
  assert(missing.length === 0, message ?? `Missing required items: [${missing.join(', ')}]`);
}

/** Asserts that x is an array and element at index i is a boolean. */
export function assertArrayItemIsBoolean<T = unknown>(x: unknown, i: number, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(typeof (x as unknown[])[i] === 'boolean', message ?? `Expected boolean at ${i}`);
}

/** Asserts that x is an array and element at index i is a string. */
export function assertArrayItemIsString<T = unknown>(x: unknown, i: number, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(typeof (x as unknown[])[i] === 'string', message ?? `Expected string at ${i}`);
}

/** Asserts that x is an array and element at index i is a number. */
export function assertArrayItemIsNumber<T = unknown>(x: unknown, i: number, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(typeof (x as unknown[])[i] === 'number', message ?? `Expected number at ${i}`);
}

/** Asserts that x is an array and element at index i is a plain object. */
export function assertArrayItemIsObject<T = unknown>(x: unknown, i: number, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  const v = (x as unknown[])[i];
  assert(typeof v === 'object' && v !== null && !Array.isArray(v), message ?? `Expected object at ${i}`);
}

/** Asserts that x is an array with at least one item whose string form includes `needle`. */
export function assertArrayIncludesString<T = unknown>(x: unknown, needle: string, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(
    (x as unknown[]).some((item) => String(item).includes(needle)),
    message ?? `Expected array to include string containing "${needle}"`,
  );
}

/** Asserts that x is an array including the exact number `needle`. */
export function assertArrayIncludesNumber<T = unknown>(x: unknown, needle: number, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(
    (x as unknown[]).some((item) => item === needle),
    message ?? `Expected array to include number ${needle}`,
  );
}

/** Asserts that x is an array including an object deep-equal to `needle`. */
export function assertArrayIncludesObject<T = unknown>(x: unknown, needle: Record<string, unknown>, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  const needleStr = JSON.stringify(needle);
  assert(
    (x as unknown[]).some((item) => JSON.stringify(item) === needleStr),
    message ?? `Expected array to include object ${needleStr}`,
  );
}

/** Asserts that x is an array whose every element is a plain object. */
export function assertArrayOnlyHasObjects<T = unknown>(x: unknown, message?: string): asserts x is Record<string, unknown>[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(
    (x as unknown[]).every((item) => typeof item === 'object' && item !== null && !Array.isArray(item)),
    message ?? `Expected array to only contain objects`,
  );
}

/** Asserts that x is an array whose every element is a string. */
export function assertArrayOnlyHasStrings<T = unknown>(x: unknown, message?: string): asserts x is string[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(
    (x as unknown[]).every((item) => typeof item === 'string'),
    message ?? `Expected array to only contain strings`,
  );
}

/** Asserts that x is an array whose every element is a number. */
export function assertArrayOnlyHasNumbers<T = unknown>(x: unknown, message?: string): asserts x is number[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(
    (x as unknown[]).every((item) => typeof item === 'number'),
    message ?? `Expected array to only contain numbers`,
  );
}

/** Asserts that x is an array and every element is falsy. */
export function assertArrayEveryIsFalsy<T = unknown>(x: unknown, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(
    (x as unknown[]).every((item) => !item),
    message ?? `Expected every item to be falsy`,
  );
}

/** Asserts that x is an array and every element is truthy. */
export function assertArrayEveryIsTruthy<T = unknown>(x: unknown, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert(
    (x as unknown[]).every((item) => !!item),
    message ?? `Expected every item to be truthy`,
  );
}

/** Assert an array has all unique items by strict equality (===). */
export function assertArrayUnique<T = unknown>(x: unknown, message = 'Expected array with unique items'): asserts x is T[] {
  assertArray<T>(x, message);
  const arr = x as unknown[];
  const seen = new Set<unknown>();
  for (const item of arr) {
    if (seen.has(item)) {
      assert(false, message);
    }
    seen.add(item);
  }
}

/** Asserts that x is an array including an element for which predicate returns true. */
export function assertArrayIncludesCondition<T = unknown>(x: unknown, predicate: (item: unknown) => boolean, message?: string): asserts x is T[] {
  assertArray<T>(x, message ?? `Expected array`);
  assert((x as unknown[]).some(predicate), message ?? `Expected array to include an item matching condition`);
}

// ---- Objects ----

/** Asserts that obj is a plain object containing the provided key. */
export function assertHasKey<O extends Record<string, unknown>, K extends string>(obj: unknown, key: K, message?: string): asserts obj is O & Record<K, unknown> {
  assertObject(obj, message ?? `Expected object`);
  assert(key in (obj as Record<string, unknown>), message ?? `Expected key "${key}"`);
}

/** Asserts that obj is a plain object containing all provided keys. */
export function assertHasKeys<O extends Record<string, unknown>, const K extends readonly string[]>(obj: unknown, ...keys: K): asserts obj is O & { [P in K[number]]: unknown } {
  assertObject(obj, `Expected object`);
  const r = obj as Record<string, unknown>;
  assert(
    keys.every((k) => k in r),
    `Expected keys: ${keys.join(', ')}`,
  );
}

/** Asserts that obj[key] strictly equals expected. */
export function assertKeyEquals<O extends Record<string, unknown>, K extends keyof O, E>(obj: unknown, key: K, expected: E, message?: string): asserts obj is O & Record<K, E> {
  assertObject(obj, message ?? `Expected object`);
  assert((obj as Record<string, unknown>)[key as string] === expected, message ?? `Expected key "${String(key)}" to equal ${JSON.stringify(expected)}`);
}

/** Asserts that obj has exactly the same set of keys as `expected`. */
export function assertSameKeys(obj: unknown, expected: Record<string, unknown>, message?: string): asserts obj is Record<string, unknown> {
  assertObject(obj, message ?? `Expected object`);
  const a = Object.keys(obj as Record<string, unknown>).sort();
  const b = Object.keys(expected).sort();
  assert(a.length === b.length && a.every((k, i) => k === b[i]), message ?? `Expected same keys`);
}

/** Asserts that `obj` contains at least all keys present in `other`. */
export function assertHasAllKeysLike<O extends Record<string, unknown>>(obj: unknown, other: O, message?: string): asserts obj is Record<string, unknown> & { [P in keyof O]: unknown } {
  // Prefer plain object semantics here to avoid array index keys confusion.
  assertPlainObject(obj, message ?? `Expected object`);
  const r = obj as Record<string, unknown>;
  const keys = Object.keys(other as Record<string, unknown>);
  const missing = keys.filter((k) => !(k in r));
  assert(missing.length === 0, message ?? `Missing required keys: [${missing.join(', ')}]`);
}

/** Ensure `obj` has all keys like `other` and return it with narrowed key presence. */
export function expectHasAllKeysLike<O extends Record<string, unknown>>(obj: unknown, other: O, message?: string): Record<string, unknown> & { [P in keyof O]: unknown } {
  assertHasAllKeysLike(obj, other, message);
  return obj as Record<string, unknown> & { [P in keyof O]: unknown };
}

/** Asserts that obj is a plain object with at least one own key. */
export function assertNonEmptyRecord(obj: unknown, message = 'Expected non-empty record'): asserts obj is Record<string, unknown> {
  assertPlainObject(obj, message);
  assert(Object.keys(obj as Record<string, unknown>).length > 0, message);
}

/** Ensure obj is a plain object with at least one own key and return it typed. */
export function expectNonEmptyRecord(obj: unknown, message?: string): Record<string, unknown> {
  assertNonEmptyRecord(obj, message);
  return obj as Record<string, unknown>;
}

/** Asserts that every value in obj is falsy. */
export function assertAllKeysFalsy(obj: unknown, message?: string): asserts obj is Record<string, unknown> {
  assertObject(obj, message ?? `Expected object`);
  assert(
    Object.values(obj as Record<string, unknown>).every((v) => !v),
    message ?? `Expected all keys to be falsy`,
  );
}

/** Asserts that every value in obj is neither null nor undefined. */
export function assertAllKeysSet(obj: unknown, message?: string): asserts obj is Record<string, unknown> {
  assertObject(obj, message ?? `Expected object`);
  const vals = Object.values(obj as Record<string, unknown>);
  assert(
    vals.every((v) => v !== null && v !== undefined),
    message ?? `Expected all keys to be set (not null/undefined)`,
  );
}
export function assertExpected<C extends new (...args: unknown[]) => InstanceType<C> | never>(x: unknown, ctor: C, asst?: (v: InstanceType<C>) => boolean): InstanceType<C> {
  if (!(typeof ctor === 'function' && x instanceof ctor)) {
    throw new AssertError('Invalid instance', { got: x, expected: ctor.name ?? ctor });
  }
  if (asst) {
    const ok = asst(x as InstanceType<C>);
    if (ok === false) {
      throw new AssertError('Additional assertion failed', { got: x, expected: asst.name || 'predicate' });
    }
  }
  return x as InstanceType<C>;
}

/** Asserts that at least one value in obj is null. */
export function assertAnyKeyNull(obj: unknown, message?: string): asserts obj is Record<string, unknown> {
  assertObject(obj, message ?? `Expected object`);
  const vals = Object.values(obj as Record<string, unknown>);
  assert(
    vals.some((v) => v === null),
    message ?? `Expected any key to be null`,
  );
}

// ---- Instance checks ----

/** Type guard: returns true if `x` is an instance of `ctor`. */
export function isInstanceOf<T>(x: unknown, ctor: new (...args: unknown[]) => T): x is T {
  return typeof ctor === 'function' && x instanceof ctor;
}

/** Asserts that `x` is an instance of the given constructor. */
export function assertInstanceOf<T>(x: unknown, ctor: new (...args: unknown[]) => T, message = 'Expected instance'): asserts x is T {
  assert(typeof ctor === 'function', 'Expected constructor');
  assert(x instanceof ctor, message);
}

/** Ensure `x` is an instance of the given constructor and return it typed. */
export function expectInstanceOf<T>(x: unknown, ctor: new (...args: unknown[]) => T, message?: string): T {
  assertInstanceOf<T>(x, ctor, message);
  return x as T;
}

// ---- Elements ----

/** Returns true if x is a DOM Element (when running in an environment with DOM). */
export function isElement(x: unknown): x is Element {
  return typeof Element !== 'undefined' && x instanceof Element;
}

/** Asserts that x is a DOM Element. */
export function assertElement(x: unknown, message = 'Expected Element'): asserts x is Element {
  assert(isElement(x), message);
} /** Asserts that x is a DOM HTML Element. */
export function assertHTMLElement(x: unknown, message = 'Expected HTMLElement'): asserts x is HTMLElement {
  assertInstanceOf(x, HTMLElement, message);
}
/** Asserts that the element has at least one child node/element. */
export function assertElementHasChildren(x: unknown, message = 'Expected element to have children'): asserts x is Element {
  assertElement(x, message);
  const el = x as Element;
  const count = (el as Element).children?.length ?? el.childNodes?.length ?? 0;
  assert(count > 0, message);
}

/** Asserts that the element has at least one child element. */
export function assertElementHasChild(x: unknown, message = 'Expected element to have a child'): asserts x is Element {
  assertElement(x, message);
  const el = x as Element;
  assert((el as Element).children?.length > 0, message);
}

/** Asserts that the element has a child matching the CSS selector. */
export function assertElementHasChildMatching(x: unknown, selector: string, message?: string): asserts x is Element {
  assertElement(x, message ?? `Expected element`);
  const el = x as Element;
  const children = Array.from((el as Element).children ?? []) as Element[];
  assert(
    children.some((c) => c.matches?.(selector)),
    message ?? `Expected child matching "${selector}"`,
  );
}

/** Asserts that the element has a descendant matching the CSS selector. */
export function assertElementHasDescendant(x: unknown, selector: string, message?: string): asserts x is Element {
  assertElement(x, message ?? `Expected element`);
  const el = x as Element;
  const found = (el as Element).querySelector?.(selector);
  assert(!!found, message ?? `Expected descendant matching "${selector}"`);
}

/** Asserts that the element has the given attribute. */
export function assertElementHasAttribute(x: unknown, name: string, message?: string): asserts x is Element {
  assertElement(x, message ?? `Expected element`);
  const el = x as Element;
  const ok = (el as Element).hasAttribute?.(name);
  assert(!!ok, message ?? `Expected element to have attribute "${name}"`);
}

/** Asserts that the element's attribute equals the expected value. */
export function assertElementAttributeEquals(x: unknown, name: string, expected: string, message?: string): asserts x is Element {
  assertElement(x, message ?? `Expected element`);
  const el = x as Element;
  const val = (el as Element).getAttribute?.(name);
  assert(val === expected, message ?? `Expected attribute "${name}" to equal "${expected}"`);
}

/** Returns true if the Element is hidden via display:none or visibility:hidden (DOM environments). */
export function isElementHidden(x: unknown): x is Element {
  if (typeof Element === 'undefined' || !(x instanceof Element)) return false;
  const computed = typeof window !== 'undefined' ? window.getComputedStyle(x) : null;
  return computed ? computed.display === 'none' || computed.visibility === 'hidden' : false;
}

/** Returns true if the Element is visible (i.e., not hidden by display/visibility). */
export function isElementVisible(x: unknown): x is Element {
  if (typeof Element === 'undefined' || !(x instanceof Element)) return false;
  const computed = typeof window !== 'undefined' ? window.getComputedStyle(x) : null;
  return computed ? computed.display !== 'none' && computed.visibility !== 'hidden' : true;
}

/** Asserts that x is an Element currently hidden by CSS (display or visibility). */
export function assertElementHidden(x: unknown, message = 'Expected element to be hidden'): asserts x is Element {
  assert(isElementHidden(x), message);
}

/** Asserts that x is an Element currently visible (not hidden by display/visibility). */
export function assertElementVisible(x: unknown, message = 'Expected element to be visible'): asserts x is Element {
  assert(isElementVisible(x), message);
}

// ---- Dates ----

/** Asserts that x is a Date earlier than `than`. */
export function assertDateEarlier(x: unknown, than: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? `Expected Date`);
  assert((x as Date).getTime() < than.getTime(), message ?? `Expected date earlier than ${than.toISOString?.() ?? than}`);
}

/** Asserts that x is a Date later than `than`. */
export function assertDateLater(x: unknown, than: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? `Expected Date`);
  assert((x as Date).getTime() > than.getTime(), message ?? `Expected date later than ${than.toISOString?.() ?? than}`);
}

/** Asserts that x is a Date within [min, max]. */
export function assertDateBetween(x: unknown, min: Date, max: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? `Expected Date`);
  const t = (x as Date).getTime();
  assert(t >= min.getTime() && t <= max.getTime(), message ?? `Expected date between ${min.toISOString?.() ?? min} and ${max.toISOString?.() ?? max}`);
}

/** Asserts that x is a Date whose full year equals `year`. */
export function assertDateYear(x: unknown, year: number, message?: string): asserts x is Date {
  assertDate(x, message ?? `Expected Date`);
  assert((x as Date).getFullYear() === year, message ?? `Expected year ${year}`);
}

// ---- Date Formats & Conversion ----

/** Supported canonical date formats */
export type DateFormat = 'ISO' | 'UNIX_MS' | 'UNIX_S' | 'RFC_2822';

/** Asserts that the input matches the expected date format shape. */
export function assertDateFormat(input: unknown, format: DateFormat, message?: string): void {
  switch (format) {
    case 'ISO': {
      assert(typeof input === 'string', message ?? 'Expected ISO string');
      const s = input as string;
      // Basic ISO 8601 test via Date.parse consistency and presence of time components
      const d = new Date(s);
      assert(!Number.isNaN(d.getTime()), message ?? 'Invalid ISO date');
      break;
    }
    case 'RFC_2822': {
      assert(typeof input === 'string', message ?? 'Expected RFC 2822 string');
      const s = input as string;
      const t = Date.parse(s);
      assert(Number.isFinite(t), message ?? 'Invalid RFC 2822 date');
      break;
    }
    case 'UNIX_MS': {
      assert(typeof input === 'number' && Number.isFinite(input as number), message ?? 'Expected UNIX ms number');
      const d = new Date(input as number);
      assert(!Number.isNaN(d.getTime()), message ?? 'Invalid UNIX ms timestamp');
      break;
    }
    case 'UNIX_S': {
      assert(typeof input === 'number' && Number.isFinite(input as number), message ?? 'Expected UNIX s number');
      const d = new Date((input as number) * 1000);
      assert(!Number.isNaN(d.getTime()), message ?? 'Invalid UNIX s timestamp');
      break;
    }
  }
}

/** Converts input of a given format to a Date (throws AssertError on failure). */
export function DateFromFormat(format: DateFormat, input: unknown, message?: string): Date {
  assertDateFormat(input, format, message);
  switch (format) {
    case 'ISO':
    case 'RFC_2822':
      return new Date(input as string);
    case 'UNIX_MS':
      return new Date(input as number);
    case 'UNIX_S':
      return new Date((input as number) * 1000);
  }
}

/** Convenience: ensure date-like input, accepting several formats. */
export function DateEnsure(input: unknown, message?: string, formats: DateFormat[] = ['ISO', 'UNIX_MS', 'UNIX_S', 'RFC_2822']): Date {
  for (const f of formats) {
    try {
      return DateFromFormat(f, input);
    } catch (e) {
      if (!(e instanceof AssertError)) throw e;
    }
  }
  throw new AssertError(message ?? 'Unrecognized date format', { got: typeof input });
}

// ---- Date ordering and relative-to-now ----

export function assertDateBefore(x: unknown, than: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assert((x as Date).getTime() < than.getTime(), message ?? 'Expected date to be before reference');
}

export function assertDateAfter(x: unknown, than: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assert((x as Date).getTime() > than.getTime(), message ?? 'Expected date to be after reference');
}

export function assertDateOnOrBefore(x: unknown, than: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assert((x as Date).getTime() <= than.getTime(), message ?? 'Expected date to be on or before reference');
}

export function assertDateOnOrAfter(x: unknown, than: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assert((x as Date).getTime() >= than.getTime(), message ?? 'Expected date to be on or after reference');
}

export function assertDateBetweenInclusive(x: unknown, min: Date, max: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  const t = (x as Date).getTime();
  assert(t >= min.getTime() && t <= max.getTime(), message ?? 'Expected date within inclusive range');
}

export function assertDateBetweenExclusive(x: unknown, min: Date, max: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  const t = (x as Date).getTime();
  assert(t > min.getTime() && t < max.getTime(), message ?? 'Expected date within exclusive range');
}

export function assertDateInPast(x: unknown, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assert((x as Date).getTime() < Date.now(), message ?? 'Expected date in the past');
}

export function assertDateInFuture(x: unknown, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assert((x as Date).getTime() > Date.now(), message ?? 'Expected date in the future');
}

export function assertDateWithinPast(x: unknown, ms: number, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assertNumber(ms, 'Expected window in ms');
  const now = Date.now();
  const t = (x as Date).getTime();
  assert(t <= now && t >= now - ms, message ?? 'Expected date within past window');
}

export function assertDateWithinFuture(x: unknown, ms: number, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assertNumber(ms, 'Expected window in ms');
  const now = Date.now();
  const t = (x as Date).getTime();
  assert(t >= now && t <= now + ms, message ?? 'Expected date within future window');
}

// ---- Date component equality ----

export function assertDateSameYear(x: unknown, other: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  assert((x as Date).getFullYear() === other.getFullYear(), message ?? 'Expected same year');
}

export function assertDateSameMonth(x: unknown, other: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  const a = x as Date;
  assert(a.getFullYear() === other.getFullYear() && a.getMonth() === other.getMonth(), message ?? 'Expected same month');
}

export function assertDateSameDay(x: unknown, other: Date, message?: string): asserts x is Date {
  assertDate(x, message ?? 'Expected Date');
  const a = x as Date;
  assert(a.getFullYear() === other.getFullYear() && a.getMonth() === other.getMonth() && a.getDate() === other.getDate(), message ?? 'Expected same day');
}

// ---- TimeSpan type and helpers ----

export type TimeSpan = { ms: number };

export function TimeSpanFromMs(ms: number): TimeSpan {
  assertNumber(ms, 'Expected ms number');
  return { ms };
}
export function TimeSpanFromSeconds(s: number): TimeSpan {
  assertNumber(s, 'Expected seconds number');
  return { ms: s * 1000 };
}
export function TimeSpanFromMinutes(m: number): TimeSpan {
  assertNumber(m, 'Expected minutes number');
  return { ms: m * 60_000 };
}
export function TimeSpanFromHours(h: number): TimeSpan {
  assertNumber(h, 'Expected hours number');
  return { ms: h * 3_600_000 };
}
export function TimeSpanFromDays(d: number): TimeSpan {
  assertNumber(d, 'Expected days number');
  return { ms: d * 86_400_000 };
}

export function DateTimeSpanBetween(a: Date, b: Date): TimeSpan {
  assertDate(a, 'Expected Date a');
  assertDate(b, 'Expected Date b');
  return { ms: Math.abs(a.getTime() - b.getTime()) };
}

export function assertDateTimeSpanLessThan(a: Date, b: Date, limit: TimeSpan, message?: string): void {
  const span = DateTimeSpanBetween(a, b);
  assert(span.ms < limit.ms, message ?? 'Expected timespan less than limit');
}

export function assertDateTimeSpanLessOrEqual(a: Date, b: Date, limit: TimeSpan, message?: string): void {
  const span = DateTimeSpanBetween(a, b);
  assert(span.ms <= limit.ms, message ?? 'Expected timespan <= limit');
}

export function assertDateBetweenWithin(a: Date, b: Date, min: TimeSpan, max: TimeSpan, message?: string): void {
  const span = DateTimeSpanBetween(a, b);
  assert(span.ms >= min.ms && span.ms <= max.ms, message ?? 'Expected timespan within bounds');
}

export function DateAddSpan(d: Date, span: TimeSpan): Date {
  assertDate(d, 'Expected Date');
  assertNumber(span?.ms as number, 'Expected TimeSpan.ms');
  return new Date(d.getTime() + span.ms);
}

export function DateSubtractSpan(d: Date, span: TimeSpan): Date {
  assertDate(d, 'Expected Date');
  assertNumber(span?.ms as number, 'Expected TimeSpan.ms');
  return new Date(d.getTime() - span.ms);
}

// ---- Nullish / Boolean convenience ----

/** Asserts that x is strictly true. */
export function assertTrue(x: unknown, message = 'Expected true'): asserts x is true {
  assert(x === true, message);
}

/** Asserts that x is strictly false. */
export function assertFalse(x: unknown, message = 'Expected false'): asserts x is false {
  assert(x === false, message);
}

/** Asserts that x is strictly null. */
export function assertNull(x: unknown, message = 'Expected null'): asserts x is null {
  assert(x === null, message);
}

/** Asserts that x is strictly undefined. */
export function assertUndefined(x: unknown, message = 'Expected undefined'): asserts x is undefined {
  assert(x === undefined, message);
}

/** Asserts that x is truthy; narrows away null/undefined/false/0/'' */
export function assertTruthy<T>(x: T, message = 'Expected truthy'): asserts x is Exclude<T, null | undefined | false | 0 | ''> {
  assert(!!x, message);
}

/** Asserts that x is falsy (null/undefined/false/0/''). */
export function assertFalsey(x: unknown, message = 'Expected falsy'): asserts x is null | undefined | false | 0 | '' {
  assert(!x, message);
}

// ==========================
// Extra number assertions
// ==========================

/** Asserts that x is a number not equal to 0. */
export function assertNumberNotZero(x: unknown, message = 'Expected non-zero number'): asserts x is number {
  assertNumber(x, message);
  assert((x as number) !== 0, message);
}

/** Asserts that x is a number strictly greater than 0. */
export function assertNumberPositive(x: unknown, message = 'Expected positive number'): asserts x is number {
  assertNumber(x, message);
  assert((x as number) > 0, message);
}

/** Asserts that x is a number >= 0. */
export function assertNumberNonNegative(x: unknown, message = 'Expected non-negative number'): asserts x is number {
  assertNumber(x, message);
  assert((x as number) >= 0, message);
}
/** Asserts that x is a number strictly less than 0. */
export function assertNumberNegative(x: unknown, message = 'Expected negative number'): asserts x is number {
  assertNumber(x, message);
  assert((x as number) < 0, message);
}

/** Asserts that x is a number <= 0. */
export function assertNumberNonPositive(x: unknown, message = 'Expected non-positive number'): asserts x is number {
  assertNumber(x, message);
  assert((x as number) <= 0, message);
}

/** Asserts that x is an integer (Number.isInteger). */
export function assertNumberInteger(x: unknown, message = 'Expected integer'): asserts x is number {
  assertNumber(x, message);
  assert(Number.isInteger(x as number), message);
}

/** Asserts that x is a safe integer (Number.isSafeInteger). */
export function assertNumberSafeInteger(x: unknown, message = 'Expected safe integer'): asserts x is number {
  assertNumber(x, message);
  assert(Number.isSafeInteger(x as number), message);
}

/** Asserts that x is a number within ±epsilon of expected. */
export function assertNumberApproxEquals(x: unknown, expected: number, epsilon = 1e-9, message?: string): asserts x is number {
  assertNumber(x, message ?? `Expected approximately ${expected} ± ${epsilon}`);
  assert(Math.abs((x as number) - expected) <= epsilon, message ?? `Expected approximately ${expected} ± ${epsilon}`);
}

// ==========================
// Array/object-of-object assertions
// ==========================

/** Asserts that x is an array of objects and each object contains `key`. */
export function assertObjectArrayAllHaveKey<K extends string>(x: unknown, key: K, message?: string): asserts x is Array<Record<string, unknown> & Record<K, unknown>> {
  assertArray(x, message ?? `Expected array`);
  assert(
    (x as unknown[]).every((item) => typeof item === 'object' && item !== null && !Array.isArray(item) && key in (item as Record<string, unknown>)),
    message ?? `Expected every object in array to have key "${key}"`,
  );
}

/** Asserts that x is an array of objects and each object contains all provided keys. */
export function assertObjectArrayEveryHasKeys<const K extends readonly string[]>(x: unknown, ...keys: K): asserts x is Array<Record<string, unknown> & { [P in K[number]]: unknown }> {
  assertArray(x, `Expected array`);
  assert(
    (x as unknown[]).every((item) => {
      if (!(typeof item === 'object' && item !== null && !Array.isArray(item))) return false;
      const r = item as Record<string, unknown>;
      return keys.every((k) => k in r);
    }),
    `Expected every object in array to have keys: ${keys.join(', ')}`,
  );
}

export function expectObjectArrayEveryHasKeys<const K extends readonly string[]>(x: unknown, ...keys: K): Array<Record<string, unknown> & { [P in K[number]]: unknown }> {
  assertObjectArrayEveryHasKeys(x, ...keys);
  return x as Array<Record<string, unknown> & { [P in K[number]]: unknown }>;
}
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    // Dates
    if (a instanceof Date || b instanceof Date) {
      if (!(a instanceof Date && b instanceof Date)) return false;
      return a.getTime() === b.getTime();
    }
    // Arrays
    if (Array.isArray(a) || Array.isArray(b)) {
      if (!(Array.isArray(a) && Array.isArray(b))) return false;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
      return true;
    }
    // Plain objects
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if (!Object.prototype.hasOwnProperty.call(bObj, k)) return false;
      if (!deepEqual(aObj[k], bObj[k])) return false;
    }
    return true;
  }
  // NaN equals NaN behavior
  return Number.isNaN(a) && Number.isNaN(b);
}

/** Asserts referential/primitive equality (===). */
export function assertEquals<T>(actual: T, expected: T, message?: string) {
  assert(actual === expected, message ?? `Expected ${JSON.stringify(actual)} === ${JSON.stringify(expected)}`);
}

/** Asserts non-equality (!==). */
export function assertNotEquals<T>(actual: T, expected: T, message?: string) {
  assert(actual !== expected, message ?? `Expected values to differ`);
}

/** Asserts deep equality using a simple structural comparison (arrays, objects, dates). */
export function assertDeepEquals<T>(actual: T, expected: T, message?: string) {
  assert(deepEqual(actual, expected), message ?? `Expected deep equality`);
}

export function _fn<Args extends unknown[], R>(defaultValue: R, impl: (...args: Args | []) => R): (...args: Args) => R {
  return (...args: Args): R => {
    try {
      return args ? impl(...args) : impl();
    } catch {
      // log as needed
      return defaultValue;
    }
  };
}

export function _fnAsync<Args extends unknown[], R>(defaultValue: R, impl: (...args: Args | []) => Promise<R>): (...args: Args) => Promise<R> {
  return async (...args: Args): Promise<R> => {
    try {
      return args && args.length > 0 ? await impl(...args) : await impl();
    } catch {
      // log as needed
      return defaultValue;
    }
  };
}

export type AssertErrorTrapOptions = {
  onError?: (err: AssertError) => void;
  /** When true (or returns true), unhandled AssertErrors are suppressed (prevent process/page break). */
  suppress?: boolean | ((err: AssertError) => boolean);
};

/**
 * Install a best-effort global trap for unhandled AssertErrors in Node and browsers.
 * - In Node: attaches 'uncaughtException' and 'unhandledRejection' listeners that swallow AssertErrors when suppress=true.
 * - In browsers: attaches 'error' and 'unhandledrejection' listeners that prevent default for AssertErrors when suppress=true.
 *
 * IMPORTANT: Suppressing unhandled errors can leave your app in an unknown state. Prefer assertRoute/confirm at boundaries.
 * This helper is provided for last-resort safety nets and diagnostics.
 */

const process = globalThis.hasOwnProperty('process') ? (globalThis as any).process : undefined;

export function installAssertErrorHandler(options: AssertErrorTrapOptions = {}) {
  const { onError, suppress } = options;
  const shouldSuppress = (err: AssertError) => (typeof suppress === 'function' ? suppress(err) : !!suppress);

  const teardown: Array<() => void> = [];

  const isNodeEnv = typeof process !== 'undefined' && !!(process as any)?.on && !!(process as any)?.versions?.node;

  if (isNodeEnv && shouldSuppress(new AssertError('probe'))) {
    const node = process as any;
    const uncaught = (err: unknown) => {
      if (err instanceof AssertError) {
        try {
          onError?.(err);
        } catch {
          /* emty */
        }
        // swallow to avoid process crash
        return;
      }
    };
    const unhandled = (reason: unknown) => {
      if (reason instanceof AssertError) {
        try {
          onError?.(reason);
        } catch {
          /* emty */
        }
        // swallow
        return;
      }
    };
    node.on('uncaughtException', uncaught);
    node.on('unhandledRejection', unhandled);
    teardown.push(() => {
      try {
        node.off('uncaughtException', uncaught);
      } catch {
        /* emty */
      }
      try {
        node.off('unhandledRejection', unhandled);
      } catch {
        /* emty */
      }
    });
  }

  if (typeof window !== 'undefined' && shouldSuppress(new AssertError('probe'))) {
    const onErrorEvt = (e: Event) => {
      const err = e instanceof ErrorEvent ? ((e as ErrorEvent).error as unknown) : undefined;
      if (err instanceof AssertError) {
        try {
          onError?.(err);
        } catch {
          /* emty */
        }
        (e as ErrorEvent).preventDefault?.();
      }
    };
    const onRejEvt = (e: PromiseRejectionEvent) => {
      const reason = e.reason as unknown;
      if (reason instanceof AssertError) {
        try {
          onError?.(reason);
        } catch {
          /* emty */
        }
        e.preventDefault();
      }
    };
    window.addEventListener('error', onErrorEvt as (e: Event) => unknown);
    window.addEventListener('unhandledrejection', onRejEvt as (e: Event) => unknown);
    teardown.push(() => {
      try {
        window.removeEventListener('error', onErrorEvt as (e: Event) => unknown);
      } catch {
        /* emty */
      }
      try {
        window.removeEventListener('unhandledrejection', onRejEvt as (e: Event) => unknown);
      } catch {
        /* emty */
      }
    });
  }

  return {
    uninstall() {
      for (const t of teardown) t();
    },
  } as const;
}
